import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const empty = { name: "", organization: "", email: "", telephone: "", category: "", message: "", privacy_ack: false, website: "" };

function Field({ id, label, required, error, children, hint }) {
  return (
    <div>
      <label htmlFor={id} className="field-label">{label}{required ? <span className="text-irid-error"> *<span className="sr-only"> required</span></span> : <span className="font-normal text-irid-slate"> (optional)</span>}</label>
      {hint && <p id={`${id}-hint`} className="mb-1.5 text-sm text-irid-slate">{hint}</p>}
      {children}
      {error && <p id={`${id}-error`} className="field-error" data-testid={`${id}-error`}><AlertCircle aria-hidden="true" className="h-4 w-4" />{error}</p>}
    </div>
  );
}

function clientValidate(v) {
  const e = {};
  if (!v.name.trim()) e.name = "Please enter your full name.";
  if (!v.email.trim()) e.email = "Please enter your email address.";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "Please enter a valid email address, for example name@example.org.";
  if (!v.category) e.category = "Please choose a category from the list.";
  if (!v.message.trim()) e.message = "Please enter your message.";
  if (!v.privacy_ack) e.privacy_ack = "Please confirm you have read the privacy notice.";
  return e;
}

export function InquiryForm({ formType, categories, categoryLabel = "Category", defaultCategory = "", skipClient = false }) {
  const [v, setV] = useState({ ...empty, category: defaultCategory });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle" });
  const started = useRef(Date.now());
  const summaryRef = useRef(null);
  const successRef = useRef(null);
  const p = `${formType}`;

  useEffect(() => { setV((x) => ({ ...x, category: defaultCategory || x.category })); }, [defaultCategory]);
  useEffect(() => { if (status.state === "success") successRef.current?.focus(); }, [status.state]);

  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const aria = (k) => ({ "aria-invalid": errors[k] ? "true" : "false", "aria-describedby": errors[k] ? `${p}-${k}-error` : undefined });

  const submit = async (e) => {
    e.preventDefault();
    const ce = skipClient ? {} : clientValidate(v);
    if (Object.keys(ce).length) {
      setErrors(ce);
      setStatus({ state: "error", message: "Please correct the highlighted fields." });
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }
    setStatus({ state: "loading" });
    try {
      const { data } = await api.post("/inquiries", { ...v, form_type: formType, elapsed_ms: Date.now() - started.current });
      setStatus({ state: "success", reference: data.reference });
      setErrors({});
    } catch (err) {
      const { message, errors: se } = apiError(err);
      setErrors(se);
      setStatus({ state: "error", message });
      setTimeout(() => summaryRef.current?.focus(), 0);
    }
  };

  if (status.state === "success") {
    return (
      <div ref={successRef} tabIndex={-1} role="status" className="rounded-xl border border-irid-green-deep/30 bg-irid-green/10 p-8" data-testid={`${p}-success`}>
        <CheckCircle2 aria-hidden="true" className="h-10 w-10 text-irid-green-deep" />
        <h3 className="mt-4 text-irid-green-deep">Thank you, your inquiry has been received</h3>
        <p className="mt-3 text-irid-ink">Your reference is <strong data-testid={`${p}-reference`}>{status.reference}</strong>. An IRID team member will review it and respond using the contact details you provided.</p>
        <button type="button" className="btn-secondary mt-6" data-testid={`${p}-new`} onClick={() => { setV({ ...empty, category: defaultCategory }); started.current = Date.now(); setStatus({ state: "idle" }); }}>Send another inquiry</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid={`${p}-form`}>
      <p className="text-sm text-irid-slate">Fields marked <span className="text-irid-error">*</span> are required.</p>
      {status.state === "error" && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="flex items-start gap-3 rounded-lg border border-irid-error/40 bg-irid-error/5 p-4 font-semibold text-irid-error" data-testid={`${p}-error-summary`}>
          <AlertCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />{status.message}
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Field id={`${p}-name`} label="Full name" required error={errors.name}>
          <input id={`${p}-name`} className="field-input" autoComplete="name" maxLength={120} value={v.name} onChange={set("name")} {...aria("name")} data-testid={`${p}-name-input`} />
        </Field>
        <Field id={`${p}-organization`} label="Organization" error={errors.organization}>
          <input id={`${p}-organization`} className="field-input" autoComplete="organization" maxLength={160} value={v.organization} onChange={set("organization")} {...aria("organization")} data-testid={`${p}-organization-input`} />
        </Field>
        <Field id={`${p}-email`} label="Email address" required error={errors.email}>
          <input id={`${p}-email`} type="email" className="field-input" autoComplete="email" value={v.email} onChange={set("email")} {...aria("email")} data-testid={`${p}-email-input`} />
        </Field>
        <Field id={`${p}-telephone`} label="Telephone" error={errors.telephone}>
          <input id={`${p}-telephone`} type="tel" className="field-input" autoComplete="tel" maxLength={30} value={v.telephone} onChange={set("telephone")} {...aria("telephone")} data-testid={`${p}-telephone-input`} />
        </Field>
      </div>
      <Field id={`${p}-category`} label={categoryLabel} required error={errors.category}>
        <select id={`${p}-category`} className="field-input" value={v.category} onChange={set("category")} {...aria("category")} data-testid={`${p}-category-select`}>
          <option value="">Select…</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field id={`${p}-message`} label="Message" required error={errors.message} hint="Up to 3,000 characters. Please do not include sensitive personal information.">
        <textarea id={`${p}-message`} rows={6} maxLength={3000} className="field-input" value={v.message} onChange={set("message")} {...aria("message")} data-testid={`${p}-message-input`} />
      </Field>
      <div className="absolute left-[-9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor={`${p}-website`}>Website (leave blank)</label>
        <input id={`${p}-website`} tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} data-testid={`${p}-honeypot`} />
      </div>
      <div className={`rounded-lg border p-4 ${errors.privacy_ack ? "border-irid-error" : "border-irid-cloud"} bg-irid-mist`}>
        <label className="flex cursor-pointer items-start gap-3 text-[15px]">
          <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 accent-[rgb(var(--irid-blue))]" checked={v.privacy_ack} onChange={set("privacy_ack")} {...aria("privacy_ack")} data-testid={`${p}-privacy-checkbox`} />
          <span>I have read the <Link to="/privacy" className="irid-link" target="_blank">IRID privacy notice</Link> and agree that IRID may use these details to respond to my inquiry.<span className="text-irid-error"> *</span></span>
        </label>
        {errors.privacy_ack && <p id={`${p}-privacy_ack-error`} className="field-error" data-testid={`${p}-privacy-error`}><AlertCircle aria-hidden="true" className="h-4 w-4" />{errors.privacy_ack}</p>}
      </div>
      <button type="submit" className="btn-primary w-full sm:w-auto" disabled={status.state === "loading"} data-testid={`${p}-submit`}>
        {status.state === "loading" && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Send inquiry
      </button>
    </form>
  );
}
